import { AxiosError } from 'axios';
import { IApis } from 'library/Apis';
import { MainRoutePath } from 'library/Routes';
import { User, UserManager } from 'oidc-client';
import React, {
  createContext,
  useCallback,
  useLayoutEffect,
  useState,
} from 'react';
import { useHistory } from 'react-router-dom';
import ApiService from 'services/ApiService';

interface AppContextType {
  currentEnv?: IApis;
  manager?: UserManager | null;
  startInterceptor?: (cb: () => void) => void;
  user?: User | null;
  setUser?: (user?: User | null) => void;
  login?: (userManager?: UserManager) => void;
  logout?: () => Promise<[void, void]> | undefined;
  loading: boolean | string;
  setLoading?: (loading: boolean | string) => void;
}

export const AuthContext = createContext<AppContextType>({
  loading: false,
});

export default function AuthContextProvider({ children, currentEnv }: any) {
  const history = useHistory();
  const [manager, setManager] = useState<UserManager | undefined>();
  const [user, setUser] = useState<User | undefined | null>();
  const [loading, setLoading] = useState<boolean | string>(false);
  // const dispatch = useDispatch();

  const startInterceptor = useCallback(
    (cb: () => void) => {
      if (user) {
        ApiService.interceptors.request.use(async (config: any) => {
          const user = await manager!?.getUser();
          config.headers = {
            ...config.headers,
            Authorization: `Bearer ${user!?.access_token}`,
          };
          return config;
        });
        cb();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [manager, user]
  );

  const logout = useCallback(() => {
    sessionStorage.removeItem('filter-assessor');

    if (manager) {
      const keysToExclude = Object.keys(window.sessionStorage).filter(
        (key) =>
          key.startsWith('oidc.') &&
          !key.includes(manager!.settings.client_id! ?? '')
      );

      keysToExclude.forEach((key) => {
        sessionStorage.removeItem(key);
      });

      setLoading(true);
      return Promise.all([
        manager.clearStaleState(),
        manager.signoutRedirect({ extraQueryParams: { state: 'state_1' } }),
      ]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [manager]);

  useLayoutEffect(() => {
    if (user) {
      ApiService.interceptors.response.use(
        (response) => response,
        (error: AxiosError) => {
          if (error.response?.status === 401) {
            logout()?.finally(() => {
              history?.push(MainRoutePath);
            });
          }
          return Promise.reject(error);
        }
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  return (
    <AuthContext.Provider
      value={{
        currentEnv,
        manager,
        login: setManager,
        logout,
        user,
        setUser,
        startInterceptor,
        loading,
        setLoading,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
